import JsEvent from '@rotomeca/event';
import { TAG_CARD_ITEM } from '../../../core/utils/constants/tags';
import BnumElementInternal from '../../bnum-element-states';
import style from './bnum-card-item.less';
import { HTMLBnumButton } from '../../atoms/button/bnum-button';
import { ClickCallback } from '../../../core/utils/types';
import { Define, SetAttr } from '../../../core/decorators';
import { CustomDom } from '../../bnum-element';
import { DEFAULT, render } from './bnum-card-item.internal/template';
import { UpdateAll } from 'core/decorators/updateAll';
import { Observe } from 'core/decorators/observe';
import { Listener } from 'core/decorators/listener';
import { onItemClickedInitializer } from './bnum-card-item.internal/listener';

//#region Global constants
const ATTRIBUTE_DISABLED = 'disabled';
const STATE_DISABLED = 'disabled';
const ROLE = 'listitem';
//#endregion Global constants

/**
 * Représente un item d'une carte `<bnum-card>` qui peut être mis dans un `bnum-card-list`.
 *
 * L'élément est considéré comme un `li` d'une liste pour des raisons d'accessibilité.
 *
 * @category Card
 *
 * @structure Item de carte
 * <bnum-card-item><p>Contenu de l'item</p></bnum-card-item>
 *
 * @structure Désactivé
 * <bnum-card-item disabled><p>Contenu de l'item</p></bnum-card-item>
 *
 * @state disabled - Actif quand l'item est désactivé
 *
 * @slot (default) - Contenu de l'item
 *
 * @attr {boolean | 'disabled' | undefined} (optional) disabled - Indique si l'item est désactivé
 *
 * @event {MouseEvent} click - Déclenché lors du clic sur l'item
 *
 * @cssvar {100%} --bnum-card-item-width-percent - Largeur en pourcentage du composant
 * @cssvar {30px} --bnum-card-item-width-modifier - Valeur soustraite à la largeur
 * @cssvar {var(--bnum-color-surface, #f6f6f7)} --bnum-card-item-background-color - Couleur de fond normale
 * @cssvar {var(--bnum-color-surface-hover, #eaeaea)} --bnum-card-item-background-color-hover - Couleur de fond au survol
 * @cssvar {var(--bnum-color-surface-active, #dfdfdf)} --bnum-card-item-background-color-active - Couleur de fond à l'état actif
 * @cssvar {pointer} --bnum-card-item-cursor - Type de curseur
 * @cssvar {15px} --bnum-card-item-padding - Espacement interne
 * @cssvar {block} --bnum-card-item-display - Type d'affichage
 */
@Define({ tag: TAG_CARD_ITEM, styles: style, template: render(DEFAULT) })
@UpdateAll()
@Observe('disabled')
export class HTMLBnumCardItem extends BnumElementInternal {
	//#region Fields
	/**
	 * Slot par défaut contenant le contenu de l'item.
	 * @protected
	 */
	protected _p_slot: HTMLSlotElement | null = null;

	/**
	 * Événement déclenché lors du clic sur l'item.
	 */
	@Listener(onItemClickedInitializer)
	accessor onitemclicked!: JsEvent<ClickCallback>;
	//#endregion Fields

	//#region Lifecycle
	/**
	 * Constructeur du composant.
	 * Initialise l'instance de l'élément.
	 */
	constructor() {
		super();
	}

	/**
	 * Construit le DOM interne du composant.
	 * Récupère le slot par défaut.
	 * @param container ShadowRoot ou HTMLElement contenant le DOM.
	 * @protected
	 */
	protected _p_buildDOM(container: CustomDom): void {
		this._p_slot = container.queryId('defaultslot');
	}

	/**
	 * Méthode appelée lors de l'attachement du composant au DOM.
	 * Définit le rôle ARIA et met à jour l'état du bouton.
	 * @protected
	 */
	@SetAttr('role', ROLE)
	protected _p_attach(): void {
		super._p_attach();

		HTMLBnumButton.ToButton(this)._p_update();
	}

	/**
	 * Met à jour le rendu du composant.
	 * @protected
	 */
	protected _p_update(): void {
		this._p_render();
	}
	//#endregion Lifecycle

	//#region Protected methods
	/**
	 * Gère le rendu et les états du composant.
	 * Met à jour l'attribut `aria-disabled` et l'état visuel.
	 * @protected
	 */
	protected _p_render() {
		this._p_clearStates();

		if (this.hasAttribute(ATTRIBUTE_DISABLED)) {
			this.setAttribute('aria-disabled', 'true');
			this._p_addState(STATE_DISABLED);
		} else this.removeAttribute('aria-disabled');
	}
	//#endregion Protected methods
}
